import pick from 'lodash/pick';
import dayjs from 'dayjs';
import { AlertsInput, AlertListingType } from 'Generated/schema';
import { getGraphqlSafeDateRangeFuture } from 'Helpers/utils';
import { AI_RISK_TAG_PREFIX, AI_RISK_TAGS } from 'Pages/Alerts/constants';
import { ListAlertsUrlParams } from './types';

export const defaultListAlertsInputParams: AlertsInput = {
  type: AlertListingType.Alert,
  createdAtAfter: getGraphqlSafeDateRangeFuture({ days: 7 })[0],
  createdAtBefore: getGraphqlSafeDateRangeFuture({ days: 7 })[1],
};

/**
 * Attempts to decode a potentially double-encoded date string and validate it.
 * Handles cases where colons might be encoded as %3A or %253A.
 * Preserves the original format when the date is already in ISO format to avoid
 * unnecessary reformatting that could strip milliseconds.
 *
 * @param dateString - The potentially encoded date string
 * @param fallbackDate - The fallback date to use if all decoding attempts fail
 * @returns A valid GraphQL DateTime string
 */
const decodeAndValidateDate = (dateString: string | undefined, fallbackDate: string): string => {
  if (!dateString) {
    return fallbackDate;
  }

  // Check if the original string is already valid, doesn't need decoding, and ends with 'Z' (UTC)
  // This preserves formats with milliseconds like "2000-01-29T00:00:00.000Z"
  // We only preserve dates ending in 'Z' (UTC), not timezone offsets, as those need normalization
  if (dayjs(dateString).isValid() && !dateString.includes('%') && dateString.endsWith('Z')) {
    // If already in valid ISO format with Z, preserve it (including milliseconds)
    return dateString;
  }

  // Try multiple decoding strategies
  const decodeAttempts = [
    dateString, // Original string
    decodeURIComponent(dateString), // Single decode
    decodeURIComponent(decodeURIComponent(dateString)), // Double decode
  ];

  // Find the first valid date after decoding
  const validDate = decodeAttempts.find(attempt => dayjs(attempt).isValid());

  if (validDate) {
    // Ensure the date is in UTC and format it correctly for GraphQL
    return dayjs(validDate).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
  }

  // If all decoding attempts fail, return fallback
  return fallbackDate;
};

const ALERTS_INPUT_KEYS: (keyof AlertsInput)[] = [
  'assigneeIds',
  'contextTags',
  'createdAtAfter',
  'createdAtBefore',
  'cursor',
  'detectionId',
  'eventCountMax',
  'eventCountMin',
  'logSources',
  'logTypes',
  'nameContains',
  'pageSize',
  'quality',
  'resourceTypes',
  'severities',
  'sortBy',
  'sortDir',
  'statuses',
  'subtypes',
  'type',
  'types',
];

export const createListAlertsInputParams = (params: ListAlertsUrlParams): AlertsInput => {
  const defaultParams = defaultListAlertsInputParams;
  const sanitizedParams = pick(params, ALERTS_INPUT_KEYS);

  // Decode and validate date parameters, handling double encoding
  const validatedParams: AlertsInput = {
    ...defaultParams,
    ...sanitizedParams,
    createdAtAfter: decodeAndValidateDate(
      sanitizedParams.createdAtAfter,
      defaultParams.createdAtAfter as string,
    ),
    createdAtBefore: decodeAndValidateDate(
      sanitizedParams.createdAtBefore,
      defaultParams.createdAtBefore as string,
    ),
  };

  return validatedParams;
};

export const extractRiskTags = (contextTags?: string[]): string[] => {
  if (!contextTags || contextTags.length === 0) {
    return [];
  }
  return contextTags.filter(tag => tag.startsWith(AI_RISK_TAG_PREFIX));
};

export const mergeContextTagsWithRisk = (
  contextTags: string[] | undefined,
  riskTags?: string[],
): string[] => {
  const base = contextTags ? contextTags.filter(tag => !tag.startsWith(AI_RISK_TAG_PREFIX)) : [];
  const validRiskTags = riskTags?.filter(tag => tag && tag.length > 0) ?? [];
  return [...base, ...validRiskTags];
};

export const isAlertClassifiedBenign = (contextTags?: string[]): boolean => {
  return extractRiskTags(contextTags).includes(AI_RISK_TAGS.benign);
};

export const riskTagLabel = (tag: string): string => {
  switch (tag) {
    case AI_RISK_TAGS.risky:
      return 'Risky';
    case AI_RISK_TAGS.inconclusive:
      return 'Inconclusive';
    case AI_RISK_TAGS.benign:
      return 'Benign';
    default:
      return '';
  }
};

export const classificationToRiskTag = (classification: string): string => {
  switch (classification.toLowerCase()) {
    case 'risky':
      return AI_RISK_TAGS.risky;
    case 'benign':
      return AI_RISK_TAGS.benign;
    case 'inconclusive':
      return AI_RISK_TAGS.inconclusive;
    default:
      return AI_RISK_TAGS.inconclusive;
  }
};

// For handling the AI generated context tags and  removing the risk tags from a contextTags array.
export const stripRiskTags = (contextTags?: string[]): string[] => {
  if (!contextTags || contextTags.length === 0) {
    return [];
  }
  return contextTags.filter(tag => !tag.startsWith(AI_RISK_TAG_PREFIX));
};
